import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { Button } from "components/button/Button";
import { Filler } from "components/filler/Filler";
import { PasswordInputGroup } from "components/input/PasswordInputGroup";
import { Loader } from "components/progress/Loader";
import type { FieldProps } from "formik";
import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import { useStores } from "stores";
import { useVerifyMfaChallenge } from "api/hooks/useVerifyMfaChallenge";
import { RESET_PASSWORD } from "api/mutations";
import { VALIDATE_RESET_TOKEN_MFA } from "api/query";
import type {
  ResetPasswordMutation,
  ResetPasswordMutationVariables,
  ValidateResetTokenMfaQuery,
  ValidateResetTokenMfaQueryVariables,
} from "graphql/generated";
import * as Yup from "yup";
import { MfaChallenge } from "../commons/mfa/MfaChallenge";
import { getRouteApi, Navigate, useNavigate } from "@tanstack/react-router";

const routeApi = getRouteApi("/successLayout/resetPassword");

const passwordStrengthValidate = Yup.object().shape({
  password: Yup.string()
    .min(12, "Please enter a valid password")
    .required("Required"),
  passwordConfirm: Yup.string()
    .nullable()
    .oneOf([Yup.ref("password"), null], "Password does not match")
    .required("Confirm Password is required"),
});

export const ResetPassword = () => {
  const { authStore, userStore } = useStores();
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = routeApi.useSearch();
  const [validateResetToken] = useLazyQuery<
    ValidateResetTokenMfaQuery,
    ValidateResetTokenMfaQueryVariables
  >(VALIDATE_RESET_TOKEN_MFA, {
    fetchPolicy: "network-only",
    // Override the default timeout of apollo-link-timeout (api/client.ts).
    context: { timeout: 30000 },
  });
  useEffectOnce(() => {
    const testResetPasswordToken = async () => {
      const { data, errors } = await validateResetToken({
        variables: { token: token! },
      });
      // `ResetPasswordWrapper` and `authStore.addMfaVariables` read the MFA
      // methods of the token's user.
      userStore.setMfaMethods(data?.validateResetTokenMfa?.mfaMethods || []);
      setError(errors?.[0]?.message ?? null);
      setIsTokenValid(!!data);
    };
    testResetPasswordToken();
  });
  if (!token) {
    authStore.setForgotPassword(true);
    return <Navigate to="/login" />;
  }
  if (isTokenValid) {
    return <ResetPasswordWrapper token={token} />;
  } else if (!error) {
    return (
      <Flex alignItems="center" justifyContent="center">
        <Loader size="lg" />
      </Flex>
    );
  } else {
    return <InvalidPasswordResetMessage />;
  }
};

const ResetPasswordWrapper = observer(function ResetPasswordWrapper({
  token,
}: {
  token: string;
}) {
  const { authStore, userStore } = useStores();
  const { verifyMfaChallenge } = useVerifyMfaChallenge();
  const [resetPassword] = useMutation<
    ResetPasswordMutation,
    ResetPasswordMutationVariables
  >(RESET_PASSWORD);
  const [isSuccess, setIsSuccess] = useState(false);
  const [taskComplete, setTaskComplete] = useState(false);
  const [showMfa, setShowMfa] = useState<boolean>(
    userStore.hasMfa && !authStore.mfaAuthCode,
  );

  useEffectOnce(() => {
    authStore.resetError();
  });

  if (taskComplete) {
    return isSuccess ? (
      <SuccessPasswordResetMessage />
    ) : (
      <InvalidPasswordResetMessage />
    );
  }
  return (
    <Formik
      initialValues={{
        password: "",
        passwordConfirm: "",
      }}
      validationSchema={passwordStrengthValidate}
      onSubmit={async ({ password, passwordConfirm }, { setSubmitting }) => {
        setSubmitting(true);
        const { data } = await resetPassword({
          variables: authStore.addMfaVariables({
            password,
            passwordConfirm,
            token,
          }),
        });
        if (data) {
          setIsSuccess(true);
        }
        setTaskComplete(true);
        setSubmitting(false);
        return;
      }}
    >
      {({ isSubmitting, values, submitForm }) => {
        return showMfa ? (
          <Flex direction={"column"} gap="10" alignItems="center" width="100%">
            <Text textStyle="h3" mb="10" textAlign="center">
              Two-factor authentication
            </Text>
            <MfaChallenge
              verify={verifyMfaChallenge}
              authVars={{ resetToken: token }}
              onVerify={() => setShowMfa(false)}
              size="lg"
              appText="Enter the code on your authenticator app to confirm password reset."
              recoverySubText="Enter recovery code to confirm"
              canUseRecoveryCode
            >
              {({
                confirm,
                isSubmitting: mfaSubmitting,
                hasError,
                isComplete,
              }) => (
                <Button
                  mt="0"
                  size="lg"
                  width="100%"
                  maxW="unset"
                  onClick={confirm}
                  isDisabled={mfaSubmitting || hasError || !isComplete}
                >
                  Confirm
                </Button>
              )}
            </MfaChallenge>
          </Flex>
        ) : (
          <Form>
            <ResetPasswordForm
              isSubmitting={isSubmitting}
              submitForm={submitForm}
              values={values}
            />
          </Form>
        );
      }}
    </Formik>
  );
});

const ResetPasswordForm = ({
  isSubmitting,
  submitForm,
  values,
}: {
  isSubmitting: boolean;
  submitForm: () => void;
  values: { password: string; passwordConfirm: string };
}) => (
  <Flex
    flexDirection="column"
    justifyContent={{ sm: "space-between" }}
    height={{ sm: "100%" }}
  >
    <Flex flexDirection="column" gap="8">
      <Heading size="h3" mb="7" textAlign="center">
        Reset your password
      </Heading>
      <Field name="password">
        {({ field, meta }: FieldProps) => (
          <PasswordInputGroup
            label="New Password"
            autocomplete="on"
            passwordType="create"
            passwordStrength={Math.max(
              0.01,
              Math.min(
                100,
                (Math.log(values?.password?.length + 1) / Math.log(13)) * 81,
              ),
            )}
            placeholder="12 characters minimum"
            error={meta.touched && meta.error ? meta.error : undefined}
            {...field}
          />
        )}
      </Field>

      <Field name="passwordConfirm">
        {({ field, meta }: FieldProps) => (
          <PasswordInputGroup
            autocomplete="on"
            passwordType="enter"
            error={meta.touched && meta.error ? meta.error : undefined}
            placeholder="12 characters or more"
            label="Confirm password"
            layoutProps={{ mb: "3" }}
            {...field}
          />
        )}
      </Field>
    </Flex>
    <Button
      type="submit"
      size="lg"
      maxW="unset"
      w="100%"
      mt={"10"}
      isDisabled={!values?.password}
      onClick={submitForm}
      isLoading={isSubmitting}
    >
      Reset my password
    </Button>
  </Flex>
);

export const SuccessPasswordResetMessage = () => {
  let navigate = useNavigate();
  const [dots, setDots] = useState(".");
  let [countNum, setCountNum] = useState(3);

  useEffect(() => {
    let interval = setInterval(() => {
      setDots(`${dots}.`);
      setCountNum(countNum - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [countNum, dots]);
  setTimeout(() => {
    navigate({ to: "/" });
  }, 3000);
  return (
    <Flex
      flexDirection="column"
      gap="4"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      <Filler.SVGFiller
        imagePath="launch/Rocket"
        variant="other"
        headline="Password successfully reset"
        mb="4"
        mt="0"
      />
      <Box>
        Redirecting to login page in {countNum} seconds {dots}
      </Box>
    </Flex>
  );
};

export const InvalidPasswordResetMessage = () => {
  const { authStore } = useStores();
  let navigate = useNavigate();
  const handleClick = () => {
    authStore.resetError();
    authStore.setForgotPassword(true);
    navigate({ to: "/" });
  };

  return (
    <Flex
      flexDirection="column"
      gap="4"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
    >
      <Filler.SVGFiller
        imagePath="error/Triangle"
        variant="other"
        headline="Invalid reset token"
        mb="4"
      />
      Sorry, this token is either invalid or expired.
      <Button
        type="reset"
        size="lg"
        maxW="unset"
        mt={"6"}
        onClick={handleClick}
      >
        Send another request
      </Button>
    </Flex>
  );
};
